import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { stripPayerIdSuffix } from '../utils/formatters'
import type { EligibilityResult, BenefitDetail, NormalizedBenefit, Provider, ConfirmedMember, CorrectionSelection } from '../types'
import type { SourceRowHighlight } from './MedicalSlideout'
import { deriveDisplayStatus, extractPlanLevelAmount, isOffDomainStcs } from '../utils/extractMedicalSummary'
import { formatPlaceOfService } from '../../shared/placeOfServiceCodes'
import { SERVICE_TYPES } from '../constants/serviceTypes'
import { IconShield, IconPercent, IconFile, IconChevronRight, IconInfo, IconPerson, IconPeople } from './Icons'
import RejectionBanner from './RejectionBanner'

/** Known STC labels — used when the 271 doesn't provide serviceTypes text. */
const STC_LABELS: Record<string, string> = Object.fromEntries(
  SERVICE_TYPES.map(s => [s.code, s.label])
)

/** A stable empty list, so a result with no benefit list does not hand the
 *  memos below a new array on every render. */
const NO_BENEFITS: BenefitDetail[] = []

function formatDate(dateStr: string): string {
  if (!dateStr || !dateStr.includes('-')) return dateStr || '-'
  const [y, m, d] = dateStr.split('-')
  return `${m}/${d}/${y}`
}

function formatCurrency(amount: number | null | undefined, decimals = 0): string {
  if (amount === null || amount === undefined) return '-'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(amount)
}

/** Normalize description for grouping: trim, uppercase, collapse whitespace. */
function normalizeDescription(desc: string | undefined): string {
  if (!desc || !desc.trim()) {
    if (import.meta.env.DEV) console.warn('[BenefitsExplorer] Benefit row with null/empty description — using __default__ for grouping')
    return '__default__'
  }
  return desc.trim().toUpperCase().replace(/\s+/g, ' ')
}

/** Human-readable label: prefer serviceTypes, fall back to type (benefit code name). */
function benefitLabel(b: BenefitDetail): string {
  const stLabels = (b.serviceTypes ?? []).filter(Boolean)
  if (stLabels.length > 0) return stLabels.join(', ')
  return b.type
}

function getLimitNotes(b: BenefitDetail): string[] {
  const notes: string[] = []
  
  // Parse limits from description (legacy/fallback)
  const match = b.description.match(/(\d+)\s*visits?\s*\/?\s*year/i) || b.description.match(/(\d+)\s*\/?\s*yr/i)
  if (match) notes.push(`LIMIT ${match[1]}/YR`)

  // Use explicit quantity fields if available (guard against null from Firestore)
  if (b.quantity != null && b.quantityQualifier) {
    // Map common X12 quantity qualifiers
    const qMap: Record<string, string> = {
      'DY': 'Days',
      'HS': 'Hours',
      'MN': 'Months',
      'VS': 'Visits',
      'YR': 'Years',
      'PT': 'Visits', // Sometimes used for physical therapy visits
    }
    const unit = qMap[b.quantityQualifier] || b.quantityQualifier
    notes.push(`Limit: ${b.quantity} ${unit}`)
  }

  // Add benefit-specific dates if present
  if (b.dates) {
    const start = b.dates.start ? formatDate(b.dates.start) : ''
    const end = b.dates.end ? formatDate(b.dates.end) : ''
    if (start && end) notes.push(`Effective: ${start} - ${end}`)
    else if (start) notes.push(`Effective from: ${start}`)
    else if (end) notes.push(`Expires: ${end}`)
  }

  return notes
}

/** Merged row: in-network and out-of-network values paired together. */
interface GroupedBenefit {
  label: string
  coverageLevel: string
  inNetworkPercent?: number
  outNetworkPercent?: number
  inNetworkAmount?: number
  outNetworkAmount?: number
  authRequired: boolean
  /** W indicator — plan has no network concept. Value duplicated to both columns. */
  networkNotApplicable: boolean
  /** null/blank indicator — payer didn't specify network direction. */
  networkUnknown: boolean
  notCovered: boolean
  description: string
  notes: string[]
  /** Raw payer network label (e.g. "PREFERRED PROVIDERS", "PARTICIPATING PROVIDER"). */
  networkLabel?: string
  /** Friendly insurance type from EB04 (e.g. "HMO - Medicare Risk"). */
  insuranceType?: string
  /** CMS Place of Service code from III segment (e.g. "10" = telehealth in patient's home). */
  placeOfService?: string
}

/**
 * Group benefits by label + coverageLevel + tierLabel, merging in-network /
 * out-of-network into a single row so both columns display correctly.
 * Tier-aware: different tiers produce separate rows.
 * networkIndicator is NOT part of the key — Y and N rows merge into one group.
 *
 * hasExplicitNetwork: when true (Y/N rows exist elsewhere in this STC),
 * W and Unknown rows still duplicate values to both columns but skip
 * the "No network distinction" / "Network not specified" badge.
 */
function groupBenefits(benefits: BenefitDetail[], hasExplicitNetwork: boolean): GroupedBenefit[] {
  const map = new Map<string, GroupedBenefit>()

  for (const b of benefits) {
    const label = benefitLabel(b)
    const tier = b.networkLabel ?? ''
    const insType = b.insuranceType ?? ''
    const pos = b.placeOfService ?? ''
    // Include normalized description, insuranceType, and placeOfService so rows with different
    // contexts (e.g. PCP vs Specialist, in-office vs telehealth) at the same STC produce
    // separate rows instead of silently overwriting each other.
    const key = `${label}||${b.coverageLevel}||${tier}||${normalizeDescription(b.description)}||${insType}||${pos}`
    let group = map.get(key)
    if (!group) {
      group = {
        label,
        coverageLevel: b.coverageLevel,
        authRequired: false,
        networkNotApplicable: false,
        networkUnknown: false,
        notCovered: false,
        description: b.description,
        notes: [],
        networkLabel: tier || undefined,
        insuranceType: b.insuranceType,
        placeOfService: b.placeOfService,
      }
      map.set(key, group)
    }

    const net = b.networkIndicator ?? ''
    const isW = net === 'W' || net === 'Not Applicable'
    const isUnknown = !isW && (!net || net === 'Unknown')

    if (isW) {
      // W = Not Applicable — duplicate to both columns
      if (b.percent !== undefined) { group.inNetworkPercent = b.percent; group.outNetworkPercent = b.percent }
      if (b.amount !== undefined) { group.inNetworkAmount = b.amount; group.outNetworkAmount = b.amount }
      // Only badge when no Y/N rows exist anywhere in this STC
      if (!hasExplicitNetwork) group.networkNotApplicable = true
    } else if (isUnknown) {
      // null/blank — duplicate to both columns
      if (b.percent !== undefined) { group.inNetworkPercent = b.percent; group.outNetworkPercent = b.percent }
      if (b.amount !== undefined) { group.inNetworkAmount = b.amount; group.outNetworkAmount = b.amount }
      if (!hasExplicitNetwork) group.networkUnknown = true
    } else if (b.inNetwork) {
      if (b.percent !== undefined) group.inNetworkPercent = b.percent
      if (b.amount !== undefined) group.inNetworkAmount = b.amount
    } else {
      if (b.percent !== undefined) group.outNetworkPercent = b.percent
      if (b.amount !== undefined) group.outNetworkAmount = b.amount
    }

    if (b.authRequired) group.authRequired = true
    // Detect not-covered status from NormalizedBenefit (structural check)
    if ('coverageStatus' in b && (b as NormalizedBenefit).coverageStatus === 'not-covered') {
      group.notCovered = true
    }
    const notes = getLimitNotes(b)
    for (const n of notes) {
      if (!group.notes.includes(n)) group.notes.push(n)
    }
  }

  // Sort by label alphabetically, then by in-network price ascending within the same label
  return Array.from(map.values()).sort((a, b) => {
    const cmp = a.label.localeCompare(b.label)
    if (cmp !== 0) return cmp
    const priceA = a.inNetworkPercent ?? a.inNetworkAmount ?? a.outNetworkPercent ?? a.outNetworkAmount ?? Infinity
    const priceB = b.inNetworkPercent ?? b.inNetworkAmount ?? b.outNetworkPercent ?? b.outNetworkAmount ?? Infinity
    return priceA - priceB
  })
}

/** A distinct deductible/OOP-max row (paired total + remaining for IN and OON). */
interface DeductibleVariant {
  description: string
  insuranceType?: string
  placeOfService?: string
  networkLabel?: string
  inTotal: number | null
  inRemaining: number | null
  oonTotal: number | null
  oonRemaining: number | null
}

/**
 * Build distinct deductible/OOP variants for a given coverage level + STC.
 * Groups by (description, insuranceType, placeOfService, networkLabel) so rows
 * differing only by those fields surface separately. Pairs TQ 23 (total) and
 * TQ 29 (remaining) within each group, split by network direction.
 */
function buildDeductibleVariants(
  benefits: BenefitDetail[],
  code: 'C' | 'G',
  coverageLevel: 'IND' | 'FAM',
  stc: string | null,
): DeductibleVariant[] {
  const matchesCoverage = (cl: string): boolean => {
    const upper = cl.toUpperCase()
    if (coverageLevel === 'IND') return upper === 'IND' || upper === 'INDIVIDUAL' || upper === 'EMPLOYEE ONLY'
    return upper === 'FAM' || upper === 'FAMILY' || upper === 'FAMILY AND DEPENDENTS'
  }

  const rows = benefits.filter(b => {
    if ((b.code ?? '') !== code) return false
    if (!matchesCoverage(b.coverageLevel)) return false
    if (stc) {
      const stcs = b.serviceTypeCodes ?? []
      if (stcs.length > 0 && !stcs.includes(stc)) return false
    }
    return true
  })

  const map = new Map<string, DeductibleVariant>()
  for (const b of rows) {
    const key = `${normalizeDescription(b.description)}||${b.insuranceType ?? ''}||${b.placeOfService ?? ''}||${b.networkLabel ?? ''}`
    let variant = map.get(key)
    if (!variant) {
      variant = {
        description: b.description,
        insuranceType: b.insuranceType,
        placeOfService: b.placeOfService,
        networkLabel: b.networkLabel,
        inTotal: null, inRemaining: null,
        oonTotal: null, oonRemaining: null,
      }
      map.set(key, variant)
    }

    const tq = (b.timeQualifier ?? '').toUpperCase()
    const isTotal = tq === '23' || tq === 'CALENDAR YEAR'
    const isRemaining = tq === '29' || tq === 'REMAINING'

    const net = (b.networkIndicator ?? '').toUpperCase()
    const isOon = net === 'N' || net === 'NO'

    if (b.amount == null) continue
    if (isOon) {
      if (isTotal) variant.oonTotal = b.amount
      else if (isRemaining) variant.oonRemaining = b.amount
      else if (variant.oonTotal === null) variant.oonTotal = b.amount
    } else {
      if (isTotal) variant.inTotal = b.amount
      else if (isRemaining) variant.inRemaining = b.amount
      else if (variant.inTotal === null) variant.inTotal = b.amount
    }
  }

  return Array.from(map.values())
}

/** Sub-row rendering for a deductible/OOP variant (under a summary row). */
function VariantRow({ v }: { v: DeductibleVariant }) {
  return (
    <tr className="benefits-table-explorer__variant-row">
      <td></td>
      <td>
        <div className="benefits-table-explorer__meta">↳ {v.description || 'Variant'}</div>
        {v.placeOfService && <div className="benefits-table-explorer__meta">Place of Service: {formatPlaceOfService(v.placeOfService)}</div>}
        {v.insuranceType && <div className="benefits-table-explorer__meta">Insurance Type: {v.insuranceType}</div>}
        {v.networkLabel && <div className="benefits-table-explorer__tier-label">{v.networkLabel}</div>}
      </td>
      <td>{v.inTotal != null ? formatCurrency(v.inTotal, 2) : '-'}</td>
      <td>{v.oonTotal != null ? formatCurrency(v.oonTotal, 2) : '-'}</td>
      <td>
        {v.inRemaining != null && (
          <div className="benefits-explorer__note">
            <div>{formatCurrency(v.inRemaining, 2)}</div>
            <div>{v.oonRemaining != null && v.oonRemaining !== v.inRemaining ? 'IN remaining' : 'remaining'}</div>
          </div>
        )}
        {v.oonRemaining != null && v.oonRemaining !== v.inRemaining && (
          <div className="benefits-explorer__note">
            <div>{formatCurrency(v.oonRemaining, 2)}</div>
            <div>OON remaining</div>
          </div>
        )}
      </td>
    </tr>
  )
}

/** Network metadata badge shown below benefit description for W or null indicators. */
function NetworkBadge({ group }: { group: GroupedBenefit }) {
  if (group.networkNotApplicable) {
    return <div className="benefits-table-explorer__network-badge">No network distinction</div>
  }
  if (group.networkUnknown) {
    return <div className="benefits-table-explorer__network-badge benefits-table-explorer__network-badge--unknown">Network not specified</div>
  }
  return null
}

function relationshipLabel(rel: string): string {
  switch (rel) {
    case 'self': return 'Subscriber'
    case 'spouse': return 'Spouse'
    case 'child': return 'Child'
    case 'other': return 'Dependent'
    default: return rel
  }
}

/** Uniquely identifies a selectable value cell (row + network column) in correction mode. */
interface SelectableValue {
  key: string
  type: 'copay' | 'coinsurance'
  amount: number | null
  percent: number | null
  network: 'in-network' | 'out-of-network'
  stc: string
  stcLabel: string
  description: string
  coverageLevel: string
}

interface BenefitsExplorerProps {
  result: EligibilityResult
  confirmedMember: ConfirmedMember
  selectedProvider: Provider
  onBack: () => void
  onEditPatient?: () => void
  highlight?: SourceRowHighlight | null
  onHighlightConsumed?: () => void
  correctionMode?: boolean
  onCorrectionSubmit?: (selection: CorrectionSelection) => void
  onCorrectionDismiss?: () => void
  /** The back button's label. The Dashboard keeps the default; a host that
   *  opens a stored check from somewhere else (the staff console's Payer
   *  Mappings board) names where back goes. */
  backLabel?: string
}

export default function BenefitsExplorer({ result, confirmedMember, selectedProvider, onBack, onEditPatient, highlight, onHighlightConsumed, correctionMode, onCorrectionSubmit, onCorrectionDismiss, backLabel = 'Back to eligibility check' }: BenefitsExplorerProps) {
  const benefits: BenefitDetail[] = result.normalizedBenefits ?? NO_BENEFITS

  // Build sidebar categories dynamically from the STCs present in the response.
  // Only include an STC if it has at least one displayable benefit row
  // (copay A, coinsurance B, or informational — excludes deductible C, OOP G, status 1).
  const { categories, coverageOnlyStcs } = useMemo(() => {
    const displayableCodes = new Set(['A', 'B'])
    const hiddenCodes = new Set(['C', 'G', '1'])
    const isDisplayable = (b: BenefitDetail) => {
      const code = b.code ?? ''
      if (displayableCodes.has(code)) {
        // copay needs an amount, coinsurance needs a percent
        if (code === 'B') return b.amount !== undefined
        if (code === 'A') return b.percent !== undefined
      }
      return !hiddenCodes.has(code)
    }

    const stcSet = new Map<string, string>() // code → label
    let hasUncategorized = false
    for (const b of benefits) {
      if (!isDisplayable(b)) continue
      const codes = b.serviceTypeCodes ?? []
      const types = b.serviceTypes ?? []
      if (codes.length === 0) {
        hasUncategorized = true
        continue
      }
      for (let i = 0; i < codes.length; i++) {
        const code = codes[i]
        if (!stcSet.has(code)) {
          stcSet.set(code, types[i] ?? STC_LABELS[code] ?? code)
        }
      }
    }

    // Include STCs that have active coverage (code '1') but no displayable financial rows
    const covOnly = new Set<string>()
    for (const b of benefits) {
      if ((b.code ?? '') !== '1') continue
      const codes = b.serviceTypeCodes ?? []
      const types = b.serviceTypes ?? []
      for (let i = 0; i < codes.length; i++) {
        const code = codes[i]
        if (!stcSet.has(code)) {
          stcSet.set(code, types[i] ?? STC_LABELS[code] ?? code)
          covOnly.add(code)
        }
      }
    }

    // Sort by code
    const entries = Array.from(stcSet.entries())
      .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
      .map(([code, label]) => ({ code, label }))

    // Add "Other" category if there are uncategorized displayable benefits
    if (hasUncategorized) {
      entries.push({ code: 'OTHER', label: 'Other / General' })
    }

    // Fallback if absolutely no categories found
    if (entries.length === 0) {
      entries.push({ code: 'ALL', label: 'All Benefits' })
    }

    return { categories: entries, coverageOnlyStcs: covOnly }
  }, [benefits])

  // Which tab opens first. Since #232 nearly every response carries an AL
  // (vision) section, and 'OTHER' is appended last — so on a response whose
  // medical rows are untagged, plain "first category" landed on Vision and the
  // deductible card rendered the VISION deductible as the headline number.
  // Prefer a medical service type, then the generic bucket, then whatever's
  // there (a genuinely vision-only response still opens on Vision).
  const defaultCategory = useMemo(() => {
    const byCode = (code: string) => categories.find(c => c.code === code)?.code
    return byCode('98') ?? byCode('96') ?? byCode('30')
      ?? categories.find(c => !isOffDomainStcs([c.code]))?.code
      ?? categories[0]?.code ?? ''
  }, [categories])

  const [selectedCategory, setSelectedCategory] = useState(defaultCategory)

  // Adjust selection during render if categories changed and current selection is invalid
  const [prevCategories, setPrevCategories] = useState(categories)
  if (prevCategories !== categories) {
    setPrevCategories(categories)
    if (!categories.find(c => c.code === selectedCategory)) {
      setSelectedCategory(defaultCategory)
    }
  }

  // Highlight source rows: auto-select STC, scroll to section, apply glow
  const tableRef = useRef<HTMLDivElement>(null)
  const [glowSection, setGlowSection] = useState<'A' | 'B' | null>(null)

  // Adjust selected category during render when highlight changes
  const [prevHighlight, setPrevHighlight] = useState(highlight)
  if (prevHighlight !== highlight) {
    setPrevHighlight(highlight)
    if (highlight && categories.some(c => c.code === highlight.stc)) {
      setSelectedCategory(highlight.stc)
    }
  }

  // Scroll to highlighted section and apply glow effect
  useEffect(() => {
    if (!highlight) return

    // Defer scroll + glow to next frame so DOM updates with new category
    const raf = requestAnimationFrame(() => {
      const container = tableRef.current
      const section = container?.querySelector(`[data-section="${highlight.code}"]`) as HTMLElement | null
      if (container && section) {
        // Scroll only the main content area — keeps patient info and sidebar in place
        const sectionTop = section.offsetTop - container.offsetTop
        container.scrollTo({ top: Math.max(0, sectionTop - 16), behavior: 'smooth' })
      }
      setGlowSection(highlight.code)

      // Clear glow after animation
      const timer = setTimeout(() => {
        setGlowSection(null)
        onHighlightConsumed?.()
      }, 2000)
      return () => clearTimeout(timer)
    })

    return () => cancelAnimationFrame(raf)
  }, [highlight, onHighlightConsumed])

  // ── Correction mode state ──────────────────────────────────────────
  const [selectedValue, setSelectedValue] = useState<SelectableValue | null>(null)
  const [explanation, setExplanation] = useState('')

  // Reset correction state during render when correctionMode changes
  const [prevCorrectionMode, setPrevCorrectionMode] = useState(correctionMode)
  if (prevCorrectionMode !== correctionMode) {
    setPrevCorrectionMode(correctionMode)
    if (correctionMode) {
      setSelectedValue(null)
      setExplanation('')
    }
  }

  const handleSelectValue = useCallback((sv: SelectableValue) => {
    setSelectedValue(prev => prev?.key === sv.key ? null : sv)
  }, [])

  const canSubmitCorrection = correctionMode && selectedValue !== null && explanation.trim().length > 0

  const handleCorrectionConfirm = useCallback(() => {
    if (!canSubmitCorrection || !selectedValue) return
    onCorrectionSubmit?.({
      type: selectedValue.type,
      amount: selectedValue.amount,
      percent: selectedValue.percent,
      network: selectedValue.network,
      stc: selectedValue.stc,
      stcLabel: selectedValue.stcLabel,
      description: selectedValue.description,
      coverageLevel: selectedValue.coverageLevel,
      explanation: explanation.trim(),
    })
  }, [canSubmitCorrection, selectedValue, explanation, onCorrectionSubmit])

  // Filter benefits to those matching the selected STC
  const filteredBenefits = useMemo(() => benefits.filter(b => {
    const stcs = b.serviceTypeCodes ?? []
    if (selectedCategory === 'ALL') return true
    if (selectedCategory === 'OTHER') return stcs.length === 0
    return stcs.includes(selectedCategory)
  }), [benefits, selectedCategory])

  // Check if ANY row in this STC has explicit Y/N network indicators.
  // When true, W and Unknown rows still duplicate values but skip the badge —
  // the plan has network distinctions, the payer just didn't tag every row.
  const hasExplicitNetwork = useMemo(() => filteredBenefits.some(b => {
    const net = (b.networkIndicator ?? '').toUpperCase()
    return net === 'Y' || net === 'YES' || net === 'N' || net === 'NO'
  }), [filteredBenefits])

  // Code A = Co-Insurance, Code B = Co-Payment (C/G handled by deductible/OOP sections)
  const coinsuranceRows = useMemo(() => groupBenefits(filteredBenefits.filter(b => (b.code ?? '') === 'A' && b.percent !== undefined), hasExplicitNetwork), [filteredBenefits, hasExplicitNetwork])
  const copayRows = useMemo(() => groupBenefits(filteredBenefits.filter(b => (b.code ?? '') === 'B' && b.amount !== undefined), hasExplicitNetwork), [filteredBenefits, hasExplicitNetwork])
  // Everything else that isn't deductible (C) or OOP max (G) — informational rows, grouped to merge in/out network
  const otherRows = useMemo(() => groupBenefits(filteredBenefits.filter(b => {
    const code = b.code ?? ''
    return code !== 'A' && code !== 'B' && code !== 'C' && code !== 'G' && code !== '1'
  }), hasExplicitNetwork), [filteredBenefits, hasExplicitNetwork])
  // Detect STCs with active coverage but no financial detail rows
  const isCoverageOnly = coverageOnlyStcs.has(selectedCategory)

  // Resolve deductible/OOP: prefer STC-specific when a specific category is selected, fall back to plan-level
  const { dedIndIn, dedIndOon, dedFamIn, dedFamOon, oopIndIn, oopIndOon, oopFamIn, oopFamOon } = useMemo(() => {
    const stc = selectedCategory !== 'ALL' && selectedCategory !== 'OTHER' ? selectedCategory : null

    const tryStc = (code: string, dir: 'in' | 'oon', level: 'individual' | 'family') => {
      if (!stc) return null
      const val = extractPlanLevelAmount(benefits, code, dir, level, stc)
      return (val.total != null || val.remaining != null) ? val : null
    }

    return {
      dedIndIn: tryStc('C', 'in', 'individual') ?? extractPlanLevelAmount(benefits, 'C', 'in', 'individual'),
      dedIndOon: tryStc('C', 'oon', 'individual') ?? extractPlanLevelAmount(benefits, 'C', 'oon', 'individual'),
      dedFamIn: tryStc('C', 'in', 'family') ?? extractPlanLevelAmount(benefits, 'C', 'in', 'family'),
      dedFamOon: tryStc('C', 'oon', 'family') ?? extractPlanLevelAmount(benefits, 'C', 'oon', 'family'),
      oopIndIn: tryStc('G', 'in', 'individual') ?? extractPlanLevelAmount(benefits, 'G', 'in', 'individual'),
      oopIndOon: tryStc('G', 'oon', 'individual') ?? extractPlanLevelAmount(benefits, 'G', 'oon', 'individual'),
      oopFamIn: tryStc('G', 'in', 'family') ?? extractPlanLevelAmount(benefits, 'G', 'in', 'family'),
      oopFamOon: tryStc('G', 'oon', 'family') ?? extractPlanLevelAmount(benefits, 'G', 'oon', 'family'),
    }
  }, [benefits, selectedCategory])

  // Variant rows — surface distinct deductible/OOP rows when payer returns
  // multiple per coverage level (e.g. PCP vs Specialist vs Telehealth).
  // Only computed for a specific STC; "ALL"/"OTHER" views stay flat.
  const variantStc = selectedCategory !== 'ALL' && selectedCategory !== 'OTHER' ? selectedCategory : null
  const dedIndVariants = useMemo(() => buildDeductibleVariants(benefits, 'C', 'IND', variantStc), [benefits, variantStc])
  const dedFamVariants = useMemo(() => buildDeductibleVariants(benefits, 'C', 'FAM', variantStc), [benefits, variantStc])
  const oopIndVariants = useMemo(() => buildDeductibleVariants(benefits, 'G', 'IND', variantStc), [benefits, variantStc])
  const oopFamVariants = useMemo(() => buildDeductibleVariants(benefits, 'G', 'FAM', variantStc), [benefits, variantStc])

  const hasDeductibleInd = dedIndIn.total != null || dedIndOon.total != null
  const hasDeductibleFam = dedFamIn.total != null || dedFamOon.total != null
  const deductibleCount = (hasDeductibleInd ? 1 : 0) + (hasDeductibleFam ? 1 : 0)
  const hasOopInd = oopIndIn.total != null || oopIndOon.total != null
  const hasOopFam = oopFamIn.total != null || oopFamOon.total != null
  const oopCount = (hasOopInd ? 1 : 0) + (hasOopFam ? 1 : 0)

  const selectedCategoryLabel = categories.find(c => c.code === selectedCategory)?.label ?? selectedCategory

  /** Build a stable key for a selectable cell. */
  function makeSelectableKey(code: string, label: string, coverageLevel: string, network: string, stc: string, description: string): string {
    return `${code}|${label}|${coverageLevel}|${network}|${stc}|${normalizeDescription(description)}`
  }

  /** Build a SelectableValue for a benefit row cell. */
  function makeSelectable(
    key: string, type: SelectableValue['type'], amount: number | null, percent: number | null,
    network: SelectableValue['network'], description: string, coverageLevel: string,
  ): SelectableValue {
    return { key, type, amount, percent, network, stc: selectedCategory, stcLabel: selectedCategoryLabel, description, coverageLevel }
  }

  // Inactive coverage — simplified layout
  if (result.status === 'inactive') {
    return (
      <main className="dashboard-main">
        <div className="results-panel">
          <div className="benefits-explorer__info benefits-explorer__info--standalone">
            <div className="benefits-explorer__info-block">
              <span className="benefits-explorer__info-label">Patient</span>
              <span className="benefits-explorer__info-value">
                {confirmedMember.firstName} {confirmedMember.lastName}
                {confirmedMember.isDependent && (
                  <span className="member-identity-banner__dep-tag">Dependent</span>
                )}
              </span>
              <span className="benefits-explorer__info-meta">
                {relationshipLabel(confirmedMember.relationship)} · DOB: {formatDate(confirmedMember.dateOfBirth)} · Member ID: {confirmedMember.memberId} · {stripPayerIdSuffix(result.payer.name)}
              </span>
            </div>
            <div className="benefits-explorer__info-block">
              <span className="benefits-explorer__info-label">Provider</span>
              <span className="benefits-explorer__info-value">{selectedProvider.firstName} {selectedProvider.lastName}</span>
              <span className="benefits-explorer__info-meta">NPI: {selectedProvider.npi}</span>
            </div>
          </div>
          <div className="inactive-message">
            <p className="inactive-message__title">Coverage Terminated</p>
            <p className="inactive-message__text">
              {result.terminationDate || result.effectiveDate
                ? <>This member's coverage was terminated on {formatDate(result.terminationDate ?? result.effectiveDate)}. Contact</>
                : <>This member's coverage is no longer active. Contact</>
              }{' '}
              the insurance company or patient for updated coverage information.
            </p>
          </div>
        </div>
        <div className="benefits-explorer__actions">
          <button className="benefits-explorer__back benefits-explorer__back--inline" onClick={onBack}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="m15 18-6-6 6-6" />
            </svg>
            {backLabel}
          </button>
          {onEditPatient && (
            <button className="btn btn--secondary" onClick={onEditPatient}>
              Edit Patient
            </button>
          )}
        </div>
      </main>
    )
  }

  // Lookup error — show rejection banner + raw rows, no cost-sharing tables
  if (result.status === 'lookup_error') {
    return (
      <div className="benefits-explorer-screen">
        <div className="benefits-explorer__actions">
          <button className="benefits-explorer__back" onClick={onBack}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="m15 18-6-6 6-6" />
            </svg>
            {backLabel}
          </button>
          {onEditPatient && (
            <button className="btn btn--secondary" onClick={onEditPatient}>
              Edit Patient
            </button>
          )}
        </div>

        <div className="results-panel benefits-explorer">
          <div className="benefits-explorer__header">
            <div className="benefits-explorer__brand">
              <span className="benefits-explorer__logo-icon"><IconShield /></span>
              <span className="benefits-explorer__logo">Benefits Explorer</span>
              <span className="benefits-explorer__plan">{result.planName}</span>
            </div>
          </div>

          <div className="benefits-explorer__info">
            <div className="benefits-explorer__info-block">
              <span className="benefits-explorer__info-label">Patient</span>
              <span className="benefits-explorer__info-value">
                {confirmedMember.firstName} {confirmedMember.lastName}
                {confirmedMember.isDependent && (
                  <span className="member-identity-banner__dep-tag">Dependent</span>
                )}
              </span>
              <span className="benefits-explorer__info-meta">
                {relationshipLabel(confirmedMember.relationship)} · DOB: {formatDate(confirmedMember.dateOfBirth)} · Member ID: {confirmedMember.memberId} · {stripPayerIdSuffix(result.payer.name)}
              </span>
            </div>
            <div className="benefits-explorer__info-block">
              <span className="benefits-explorer__info-label">Provider</span>
              <span className="benefits-explorer__info-value">{selectedProvider.firstName} {selectedProvider.lastName}</span>
              <span className="benefits-explorer__info-meta">NPI: {selectedProvider.npi}</span>
            </div>
          </div>

          <RejectionBanner result={result} />

          <div className="benefits-explorer__body">
            <main className="benefits-explorer__main benefits-explorer__main--full">
              <div className="benefits-explorer__table">
                <table className="benefits-table-explorer">
                  <thead>
                    <tr>
                      <th>TYPE</th>
                      <th>CODE</th>
                      <th>DESCRIPTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {benefits.map((b, i) => (
                      <tr key={i}>
                        <td>{b.type}</td>
                        <td>{b.code ?? '-'}</td>
                        <td>{b.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </main>
          </div>
        </div>
      </div>
    )
  }

  // Active coverage — full benefits explorer

  return (
    <div className="benefits-explorer-screen">
      {!correctionMode && (
        <button className="benefits-explorer__back" onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="m15 18-6-6 6-6" />
          </svg>
          {backLabel}
        </button>
      )}

      <div className={`results-panel benefits-explorer${correctionMode ? ' benefits-explorer--correction' : ''}`}>
        <div className="benefits-explorer__header">
          <div className="benefits-explorer__brand">
            <span className="benefits-explorer__logo-icon"><IconShield /></span>
            <span className="benefits-explorer__logo">Benefits Explorer</span>
            <span className="benefits-explorer__plan">{result.planName}</span>
            {correctionMode && (
              <span className="correction-mode-badge">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                Correction Mode
              </span>
            )}
          </div>
          {correctionMode && (
            <button className="benefits-explorer__close" onClick={onCorrectionDismiss} aria-label="Close correction mode">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        {correctionMode && (
          <p className="correction-mode-instruction">
            Select the correct copay or coinsurance row, then explain why the AI's choice was wrong.
          </p>
        )}

        <div className="benefits-explorer__info">
          <div className="benefits-explorer__info-block">
            <span className="benefits-explorer__info-label">Coverage Verified For</span>
            <span className="benefits-explorer__info-value">
              {confirmedMember.firstName} {confirmedMember.lastName}
              {confirmedMember.isDependent && (
                <span className="member-identity-banner__dep-tag">Dependent</span>
              )}
            </span>
            <span className="benefits-explorer__info-meta">
              {relationshipLabel(confirmedMember.relationship)} · DOB: {formatDate(confirmedMember.dateOfBirth)} · Member ID: {confirmedMember.memberId} · {stripPayerIdSuffix(result.payer.name)}
            </span>
          </div>
          <div className="benefits-explorer__info-block">
            <span className="benefits-explorer__info-label">Provider</span>
            <span className="benefits-explorer__info-value">{selectedProvider.firstName} {selectedProvider.lastName}</span>
            <span className="benefits-explorer__info-meta">NPI: {selectedProvider.npi}</span>
          </div>
        </div>

        <div className="benefits-explorer__body">
          <aside className="benefits-explorer__sidebar">
            <p className="benefits-explorer__sidebar-title">
              SERVICE TYPE CATEGORIES ({categories.length})
            </p>
            {categories.map(cat => (
              <button
                key={cat.code}
                className={`benefits-explorer__nav-item ${selectedCategory === cat.code ? 'benefits-explorer__nav-item--active' : ''}`}
                onClick={() => { setSelectedCategory(cat.code); tableRef.current?.scrollTo(0, 0) }}
              >
                <span>STC {cat.code} {cat.label}</span>
                <span className="benefits-explorer__nav-arrow"><IconChevronRight /></span>
              </button>
            ))}
          </aside>

          <main className="benefits-explorer__main" ref={tableRef}>
            <div className="benefits-explorer__main-header">
              <h2 className="benefits-explorer__main-title">
                STC {selectedCategory} {categories.find(c => c.code === selectedCategory)?.label}
              </h2>
              <div className="benefits-explorer__main-header-right">
                <span className={`benefits-explorer__status benefits-explorer__status--${deriveDisplayStatus(result)}`}>
                  {deriveDisplayStatus(result) === 'active' ? 'Active' : 'Unconfirmed Eligibility'}
                </span>
                <span className="benefits-explorer__plan-context">
                  {result.groupName ?? '-'}
                </span>
              </div>
            </div>

            <div className="benefits-explorer__table">
              <table className="benefits-table-explorer">
                <colgroup>
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                </colgroup>
                <thead>
                  <tr>
                    <th>TYPE</th>
                    <th>BENEFIT / DESCRIPTION</th>
                    <th>IN NETWORK</th>
                    <th>OUT NETWORK</th>
                    <th>LIMITS / NOTES</th>
                  </tr>
                </thead>
                <tbody>
                  {deductibleCount > 0 && (
                    <>
                      <tr className="benefits-table-explorer__section-row">
                        <td colSpan={5} className="benefits-table-explorer__section-header">
                          <span className="benefits-table-explorer__section-icon"><IconShield /></span>
                          DEDUCTIBLE ({deductibleCount})
                        </td>
                      </tr>
                      {hasDeductibleInd && (
                        <tr>
                          <td><IconPerson /></td>
                          <td>
                            <div>Annual Deductible</div>
                            <div className="benefits-table-explorer__meta">Calendar Year · <IconPerson /> Individual</div>
                          </td>
                          <td>{formatCurrency(dedIndIn.total, 2)}</td>
                          <td>{formatCurrency(dedIndOon.total, 2)}</td>
                          <td>
                            {dedIndIn.remaining != null && (
                              <div className="benefits-explorer__note">
                                <div>{formatCurrency(dedIndIn.remaining, 2)}</div>
                                <div>{dedIndOon.remaining != null && dedIndOon.remaining !== dedIndIn.remaining ? 'IN remaining' : 'remaining'}</div>
                              </div>
                            )}
                            {dedIndOon.remaining != null && dedIndOon.remaining !== dedIndIn.remaining && (
                              <div className="benefits-explorer__note">
                                <div>{formatCurrency(dedIndOon.remaining, 2)}</div>
                                <div>OON remaining</div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                      {hasDeductibleInd && dedIndVariants.length > 1 && dedIndVariants.map((v, i) => <VariantRow key={`ded-ind-var-${i}`} v={v} />)}
                      {hasDeductibleFam && (
                        <tr>
                          <td><IconPeople /></td>
                          <td>
                            <div>Annual Deductible</div>
                            <div className="benefits-table-explorer__meta">Calendar Year · Family</div>
                          </td>
                          <td>{formatCurrency(dedFamIn.total, 2)}</td>
                          <td>{formatCurrency(dedFamOon.total, 2)}</td>
                          <td>
                            {dedFamIn.remaining != null && (
                              <div className="benefits-explorer__note">
                                <div>{formatCurrency(dedFamIn.remaining, 2)}</div>
                                <div>{dedFamOon.remaining != null && dedFamOon.remaining !== dedFamIn.remaining ? 'IN remaining' : 'remaining'}</div>
                              </div>
                            )}
                            {dedFamOon.remaining != null && dedFamOon.remaining !== dedFamIn.remaining && (
                              <div className="benefits-explorer__note">
                                <div>{formatCurrency(dedFamOon.remaining, 2)}</div>
                                <div>OON remaining</div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                      {hasDeductibleFam && dedFamVariants.length > 1 && dedFamVariants.map((v, i) => <VariantRow key={`ded-fam-var-${i}`} v={v} />)}
                    </>
                  )}

                  {oopCount > 0 && (
                    <>
                      <tr className="benefits-table-explorer__section-row">
                        <td colSpan={5} className="benefits-table-explorer__section-header">
                          <span className="benefits-table-explorer__section-icon"><IconShield /></span>
                          OUT-OF-POCKET MAX ({oopCount})
                        </td>
                      </tr>
                      {hasOopInd && (
                        <tr>
                          <td><IconPerson /></td>
                          <td>
                            <div>Out-of-Pocket Maximum</div>
                            <div className="benefits-table-explorer__meta">Calendar Year · <IconPerson /> Individual</div>
                          </td>
                          <td>{formatCurrency(oopIndIn.total, 2)}</td>
                          <td>{formatCurrency(oopIndOon.total, 2)}</td>
                          <td>
                            {oopIndIn.remaining != null && (
                              <div className="benefits-explorer__note">
                                <div>{formatCurrency(oopIndIn.remaining, 2)}</div>
                                <div>{oopIndOon.remaining != null && oopIndOon.remaining !== oopIndIn.remaining ? 'IN remaining' : 'remaining'}</div>
                              </div>
                            )}
                            {oopIndOon.remaining != null && oopIndOon.remaining !== oopIndIn.remaining && (
                              <div className="benefits-explorer__note">
                                <div>{formatCurrency(oopIndOon.remaining, 2)}</div>
                                <div>OON remaining</div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                      {hasOopInd && oopIndVariants.length > 1 && oopIndVariants.map((v, i) => <VariantRow key={`oop-ind-var-${i}`} v={v} />)}
                      {hasOopFam && (
                        <tr>
                          <td><IconPeople /></td>
                          <td>
                            <div>Out-of-Pocket Maximum</div>
                            <div className="benefits-table-explorer__meta">Calendar Year · Family</div>
                          </td>
                          <td>{formatCurrency(oopFamIn.total, 2)}</td>
                          <td>{formatCurrency(oopFamOon.total, 2)}</td>
                          <td>
                            {oopFamIn.remaining != null && (
                              <div className="benefits-explorer__note">
                                <div>{formatCurrency(oopFamIn.remaining, 2)}</div>
                                <div>{oopFamOon.remaining != null && oopFamOon.remaining !== oopFamIn.remaining ? 'IN remaining' : 'remaining'}</div>
                              </div>
                            )}
                            {oopFamOon.remaining != null && oopFamOon.remaining !== oopFamIn.remaining && (
                              <div className="benefits-explorer__note">
                                <div>{formatCurrency(oopFamOon.remaining, 2)}</div>
                                <div>OON remaining</div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                      {hasOopFam && oopFamVariants.length > 1 && oopFamVariants.map((v, i) => <VariantRow key={`oop-fam-var-${i}`} v={v} />)}
                    </>
                  )}

                  {isCoverageOnly && (
                    <tr className="benefits-table-explorer__coverage-only-row">
                      <td colSpan={5}>
                        <div className="benefits-explorer__coverage-info">
                          <IconShield />
                          <span>Covered – No cost details returned by payer</span>
                        </div>
                      </td>
                    </tr>
                  )}

                  {(copayRows.length > 0 || coinsuranceRows.length > 0) && (
                    <>
                      {copayRows.length > 0 && (
                        <>
                          <tr className="benefits-table-explorer__section-row" data-section="B">
                            <td colSpan={5} className="benefits-table-explorer__section-header">
                              <span className="benefits-table-explorer__section-icon"><IconFile /></span>
                              CO-PAYMENT ({copayRows.length})
                            </td>
                          </tr>
                          {copayRows.map((g, i) => {
                            const inKey = g.inNetworkAmount !== undefined ? makeSelectableKey('B', g.label, g.coverageLevel, 'in-network', selectedCategory, g.description) : null
                            const outKey = g.outNetworkAmount !== undefined ? makeSelectableKey('B', g.label, g.coverageLevel, 'out-of-network', selectedCategory, g.description) : null
                            const isInSelected = selectedValue?.key === inKey
                            const isOutSelected = selectedValue?.key === outKey
                            const isRowSelected = isInSelected || isOutSelected
                            const rowClasses = [
                              g.notCovered ? 'benefits-table-explorer__row--not-covered' : '',
                              glowSection === 'B' ? 'benefits-table-explorer__row--glow' : '',
                              correctionMode ? 'benefits-table-explorer__row--selectable' : '',
                              isRowSelected ? 'benefits-table-explorer__row--selected' : '',
                            ].filter(Boolean).join(' ')

                            const rowKey = inKey ?? outKey
                            const rowClickValue = rowKey
                              ? makeSelectable(rowKey, 'copay', inKey ? (g.inNetworkAmount ?? null) : (g.outNetworkAmount ?? null), null, inKey ? 'in-network' : 'out-of-network', g.description, g.coverageLevel)
                              : null

                            return (
                              <tr
                                key={`copay-${i}`}
                                className={rowClasses}
                                onClick={correctionMode && rowClickValue ? () => handleSelectValue(rowClickValue) : undefined}
                              >
                                <td>
                                  {correctionMode ? (
                                    <span className={`benefits-table-explorer__select-indicator${isRowSelected ? ' benefits-table-explorer__select-indicator--checked' : ''}`}>
                                      {isRowSelected && (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" /></svg>
                                      )}
                                    </span>
                                  ) : (
                                    <IconFile />
                                  )}
                                </td>
                                <td>
                                  <div>
                                    {g.label}
                                    {g.notCovered && <span className="benefits-table-explorer__not-covered-badge">Not Covered</span>}
                                  </div>
                                  <div className="benefits-table-explorer__meta">{g.description}</div>
                                  {g.placeOfService && <div className="benefits-table-explorer__meta">Place of Service: {formatPlaceOfService(g.placeOfService)}</div>}
                                  {g.insuranceType && <div className="benefits-table-explorer__meta">Insurance Type: {g.insuranceType}</div>}
                                  {g.networkLabel && g.networkLabel !== g.description && <div className="benefits-table-explorer__tier-label">{g.networkLabel}</div>}
                                </td>
                                <td
                                  className={isInSelected ? 'benefits-table-explorer__cell--selected' : ''}
                                  onClick={correctionMode && inKey ? (e) => { e.stopPropagation(); handleSelectValue(makeSelectable(inKey, 'copay', g.inNetworkAmount ?? null, null, 'in-network', g.description, g.coverageLevel)) } : undefined}
                                >
                                  {g.inNetworkAmount !== undefined ? formatCurrency(g.inNetworkAmount, 2) : '-'}
                                </td>
                                <td
                                  className={isOutSelected ? 'benefits-table-explorer__cell--selected' : ''}
                                  onClick={correctionMode && outKey ? (e) => { e.stopPropagation(); handleSelectValue(makeSelectable(outKey, 'copay', g.outNetworkAmount ?? null, null, 'out-of-network', g.description, g.coverageLevel)) } : undefined}
                                >
                                  {g.outNetworkAmount !== undefined ? formatCurrency(g.outNetworkAmount, 2) : '-'}
                                </td>
                                <td>
                                  {g.authRequired && <div className="benefits-table-explorer__meta">Prior Authorization Required</div>}
                                  {g.notes.map((note) => (
                                    <div key={note} className="benefits-table-explorer__meta">{note}</div>
                                  ))}
                                  <NetworkBadge group={g} />
                                </td>
                              </tr>
                            )
                          })}
                        </>
                      )}

                      {coinsuranceRows.length > 0 && (
                        <>
                          <tr className="benefits-table-explorer__section-row" data-section="A">
                            <td colSpan={5} className="benefits-table-explorer__section-header">
                              <span className="benefits-table-explorer__section-icon"><IconPercent /></span>
                              % CO-INSURANCE ({coinsuranceRows.length})
                            </td>
                          </tr>
                          {coinsuranceRows.map((g, i) => {
                            const inKey = g.inNetworkPercent !== undefined ? makeSelectableKey('A', g.label, g.coverageLevel, 'in-network', selectedCategory, g.description) : null
                            const outKey = g.outNetworkPercent !== undefined ? makeSelectableKey('A', g.label, g.coverageLevel, 'out-of-network', selectedCategory, g.description) : null
                            const isInSelected = selectedValue?.key === inKey
                            const isOutSelected = selectedValue?.key === outKey
                            const isRowSelected = isInSelected || isOutSelected
                            const rowClasses = [
                              g.notCovered ? 'benefits-table-explorer__row--not-covered' : '',
                              glowSection === 'A' ? 'benefits-table-explorer__row--glow' : '',
                              correctionMode ? 'benefits-table-explorer__row--selectable' : '',
                              isRowSelected ? 'benefits-table-explorer__row--selected' : '',
                            ].filter(Boolean).join(' ')

                            const rowKey = inKey ?? outKey
                            const rowClickValue = rowKey
                              ? makeSelectable(rowKey, 'coinsurance', null, inKey ? (g.inNetworkPercent ?? null) : (g.outNetworkPercent ?? null), inKey ? 'in-network' : 'out-of-network', g.description, g.coverageLevel)
                              : null

                            return (
                              <tr
                                key={`coinsurance-${i}`}
                                className={rowClasses}
                                onClick={correctionMode && rowClickValue ? () => handleSelectValue(rowClickValue) : undefined}
                              >
                                <td>
                                  {correctionMode ? (
                                    <span className={`benefits-table-explorer__select-indicator${isRowSelected ? ' benefits-table-explorer__select-indicator--checked' : ''}`}>
                                      {isRowSelected && (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" /></svg>
                                      )}
                                    </span>
                                  ) : (
                                    <IconPercent />
                                  )}
                                </td>
                                <td>
                                  <div>
                                    {g.label}
                                    {g.notCovered && <span className="benefits-table-explorer__not-covered-badge">Not Covered</span>}
                                  </div>
                                  {g.description && g.description !== g.label && (
                                    <div className="benefits-table-explorer__meta">{g.description}</div>
                                  )}
                                  {g.placeOfService && <div className="benefits-table-explorer__meta">Place of Service: {formatPlaceOfService(g.placeOfService)}</div>}
                                  {g.insuranceType && <div className="benefits-table-explorer__meta">Insurance Type: {g.insuranceType}</div>}
                                  <div className="benefits-table-explorer__meta"><IconPerson /> {g.coverageLevel}</div>
                                  {g.networkLabel && g.networkLabel !== g.description && <div className="benefits-table-explorer__tier-label">{g.networkLabel}</div>}
                                </td>
                                <td
                                  className={isInSelected ? 'benefits-table-explorer__cell--selected' : ''}
                                  onClick={correctionMode && inKey ? (e) => { e.stopPropagation(); handleSelectValue(makeSelectable(inKey, 'coinsurance', null, g.inNetworkPercent ?? null, 'in-network', g.description, g.coverageLevel)) } : undefined}
                                >
                                  {g.inNetworkPercent !== undefined ? `${g.inNetworkPercent}%` : '-'}
                                </td>
                                <td
                                  className={isOutSelected ? 'benefits-table-explorer__cell--selected' : ''}
                                  onClick={correctionMode && outKey ? (e) => { e.stopPropagation(); handleSelectValue(makeSelectable(outKey, 'coinsurance', null, g.outNetworkPercent ?? null, 'out-of-network', g.description, g.coverageLevel)) } : undefined}
                                >
                                  {g.outNetworkPercent !== undefined ? `${g.outNetworkPercent}%` : '-'}
                                </td>
                                <td>
                                  {g.authRequired && <div className="benefits-table-explorer__meta">Prior Authorization Required</div>}
                                  {g.notes.map((note) => (
                                    <div key={note} className="benefits-table-explorer__meta">{note}</div>
                                  ))}
                                  <NetworkBadge group={g} />
                                </td>
                              </tr>
                            )
                          })}
                        </>
                      )}
                    </>
                  )}

                  {otherRows.length > 0 && (
                    <>
                      <tr className="benefits-table-explorer__section-row">
                        <td colSpan={5} className="benefits-table-explorer__section-header">
                          <span className="benefits-table-explorer__section-icon"><IconInfo /></span>
                          ADDITIONAL INFORMATION ({otherRows.length})
                        </td>
                      </tr>
                      {otherRows.map((g, i) => (
                        <tr key={`other-${i}`} className={g.notCovered ? 'benefits-table-explorer__row--not-covered' : ''}>
                          <td><IconInfo /></td>
                          <td>
                            <div>
                              {g.label}
                              {g.notCovered && <span className="benefits-table-explorer__not-covered-badge">Not Covered</span>}
                            </div>
                            {g.description && g.description !== g.label && (
                              <div className="benefits-table-explorer__meta">{g.description}</div>
                            )}
                            {g.placeOfService && <div className="benefits-table-explorer__meta">Place of Service: {formatPlaceOfService(g.placeOfService)}</div>}
                            {g.insuranceType && <div className="benefits-table-explorer__meta">Insurance Type: {g.insuranceType}</div>}
                          </td>
                          <td>{g.inNetworkAmount !== undefined ? formatCurrency(g.inNetworkAmount, 2) : g.inNetworkPercent !== undefined ? `${g.inNetworkPercent}%` : '-'}</td>
                          <td>{g.outNetworkAmount !== undefined ? formatCurrency(g.outNetworkAmount, 2) : g.outNetworkPercent !== undefined ? `${g.outNetworkPercent}%` : '-'}</td>
                          <td>
                            {g.authRequired && <div className="benefits-table-explorer__meta">Prior Authorization Required</div>}
                            {g.notes.map((note) => (
                              <div key={note} className="benefits-table-explorer__meta">{note}</div>
                            ))}
                            <NetworkBadge group={g} />
                          </td>
                        </tr>
                      ))}
                    </>
                  )}

                </tbody>
              </table>
            </div>
          </main>
        </div>
      </div>

      {correctionMode && (
        <div className="correction-footer">
          {selectedValue ? (
            <div className="correction-footer__selection">
              <span className="correction-footer__dot" />
              <span>Selected:</span>
              <strong>{selectedValue.type === 'copay' ? 'Co-payment' : 'Co-insurance'}</strong>
              <span>&middot;</span>
              <strong>
                {selectedValue.type === 'copay'
                  ? (selectedValue.amount !== null ? formatCurrency(selectedValue.amount, 2) : '-')
                  : `${selectedValue.percent ?? 0}%`}
              </strong>
              <span>{selectedValue.network}</span>
            </div>
          ) : (
            <div className="correction-footer__selection correction-footer__selection--empty">
              Select a copay or coinsurance row above
            </div>
          )}
          <textarea
            className="correction-footer__explanation"
            placeholder="Explain why the AI's interpretation was incorrect (required)..."
            value={explanation}
            onChange={e => setExplanation(e.target.value)}
            rows={2}
          />
          <div className="correction-footer__actions">
            <button className="correction-footer__cancel" onClick={onCorrectionDismiss}>
              Cancel
            </button>
            <button
              className="correction-footer__submit"
              disabled={!canSubmitCorrection}
              onClick={handleCorrectionConfirm}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Use this value
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
